import { string } from 'prop-types';
import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { createResourceLocatorString } from '../../../../util/routes';
import LandingServicesPage from '../../LandingServicesPage';
import css from './LatestServices.module.css';

/**
 * LatestServices — "Recently listed services" section.
 * Visual reference: lovable_template/src/components/site/LastMinute.tsx
 *
 *   <section bg-foreground>
 *     <div max-w-7xl>
 *       <header (flex)>
 *         <div>                                   ← left block
 *           <pill bg-primary/20 text-primary>     ← "Just listed" + Sparkles
 *           <h2>Recently listed services</h2>
 *           <p>Fresh services …</p>
 *         </div>
 *         <Button secondary rounded-full>          ← "Browse all services"
 *       </header>
 *       <grid 4 cols>                              ← cards (via LandingServicesPage)
 *     </div>
 *   </section>
 */

// Inline Lucide-style Sparkles icon (no new dependency).
const SparklesIcon = props => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  >
    <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
    <path d="M20 3v4" />
    <path d="M22 5h-4" />
    <path d="M4 17v2" />
    <path d="M5 18H3" />
  </svg>
);

const LatestServices = ({ intl, history, routeConfiguration }) => {
  const arabicLanguage =
    typeof window !== 'undefined' && localStorage.getItem('language') === 'Arabic';

  // Geolocation kept so the embedded service cards can show distances.
  const [currLocation, setCurrLocation] = useState({});

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      position => {
        const { latitude, longitude } = position.coords;
        setCurrLocation({
          lat: parseFloat(latitude.toFixed(3)),
          longitude: parseFloat(longitude.toFixed(3)),
        });
      },
      () => {
        // Silent: keep cards working without distance if denied.
      }
    );
  }, []);

  return (
    <section
      className={classNames(
        css.latestServicesWrapper,
        arabicLanguage && css.arabicDirection
      )}
    >
      <div className={css.inner}>
        <div className={css.headingArea}>
          <div className={css.headingLeft}>
            <div className={css.justListedPill}>
              <SparklesIcon className={css.justListedIcon} />
              {intl.formatMessage({ id: 'LatestServices.justListed' })}
            </div>
            <h2 className={css.heading}>
              {intl.formatMessage({ id: 'LatestServices.heading' })}
            </h2>
            <p className={css.subheading}>
              {intl.formatMessage({ id: 'LatestServices.subheading' })}
            </p>
          </div>
          {history && routeConfiguration && (
            <button
              type="button"
              className={css.browseAllButton}
              onClick={() =>
                history.push(
                  createResourceLocatorString(
                    'AlgoliaSearchPage',
                    routeConfiguration,
                    { listingType: 'services' },
                    {}
                  )
                )
              }
            >
              {intl.formatMessage({ id: 'LatestServices.browseAllServices' })}
            </button>
          )}
        </div>

        <div className={css.searvicesCard}>
          <LandingServicesPage currLocation={currLocation} />
        </div>
      </div>
    </section>
  );
};

LatestServices.defaultProps = {
  className: null,
  rootClassName: null,
};

LatestServices.propTypes = {
  className: string,
  rootClassName: string,
};

export default LatestServices;
